import { useEffect, useState } from 'react';

import { getAgencyConfig } from '../services/agencyConfig';
import { useStore } from '../store';

// A report run reads this once per run, so keep one answer per agency for a
// short while instead of fetching the whole agency config for every run.
const TTL_MS = 60_000;

type Entry = { agencyId: string; at: number; value: Promise<boolean> };
let entry: Entry | null = null;

/**
 * Whether the agency keeps invoice-billed claims off its AR reports.
 *
 * A failed read answers false: the reports then show every claim, which is the
 * behaviour of an agency that never changed the setting.
 */
export function loadExcludeInvoiceClaims(
  agencyId: string | undefined
): Promise<boolean> {
  if (!agencyId) return Promise.resolve(false);
  const now = Date.now();
  if (entry && entry.agencyId === agencyId && now - entry.at < TTL_MS) {
    return entry.value;
  }
  const value = getAgencyConfig()
    .then(cfg => cfg.exclude_invoice_claims_from_ar === true)
    .catch(err => {
      console.error('Failed to load the AR report setting:', err);
      if (entry?.value === value) entry = null;
      return false;
    });
  entry = { agencyId, at: now, value };
  return value;
}

/** Forget the stored answer, e.g. right after the setting is saved. */
export function resetAgencyReportPolicy(): void {
  entry = null;
}

/** The setting for the signed-in agency; false until it has loaded. */
export function useExcludeInvoiceClaims(): boolean {
  const agencyId = useStore(state => state.user?.agency?.id);
  const [value, setValue] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void loadExcludeInvoiceClaims(agencyId).then(v => {
      if (!cancelled) setValue(v);
    });
    return () => {
      cancelled = true;
    };
  }, [agencyId]);
  return value;
}
